import React, { useEffect, useState, useRef, useCallback } from 'react';
import { Terminal } from '@xterm/xterm';
import { FitAddon } from '@xterm/addon-fit';
import '@xterm/xterm/css/xterm.css';

// ULF log entry from backend
interface UlfLogEntry {
  id: number;
  session_id: number;
  ts: number;
  ts_est: string;
  layer: string;
  level: string;
  message: string;
  request_id?: string;
}

interface LogHistoryTerminalProps {
  visible: boolean;
  onStatusChange?: (status: 'disconnected' | 'connecting' | 'connected') => void;
}

// ANSI color codes for formatting
const COLORS = {
  reset: '\x1b[0m',
  dim: '\x1b[2m',
  bold: '\x1b[1m',
  cyan: '\x1b[36m',
  green: '\x1b[32m',
  yellow: '\x1b[33m',
  blue: '\x1b[34m',
  magenta: '\x1b[35m',
  red: '\x1b[31m',
  gray: '\x1b[90m',
  orange: '\x1b[38;5;208m',
  purple: '\x1b[38;5;135m',
};

// Layer colors matching ULF_LAYERS in server.js
const LAYER_COLORS: Record<string, { color: string; arrow: string }> = {
  RUST_IN:   { color: COLORS.orange, arrow: '←' },
  RUST_OUT:  { color: COLORS.orange, arrow: '→' },
  JAVA_IN:   { color: COLORS.purple, arrow: '←' },
  JAVA_OUT:  { color: COLORS.purple, arrow: '→' },
  LUA_S_IN:  { color: COLORS.cyan,   arrow: '←' },
  LUA_S_OUT: { color: COLORS.cyan,   arrow: '→' },
  LUA_C:     { color: COLORS.green,  arrow: '◆' },
};

const LogHistoryTerminal: React.FC<LogHistoryTerminalProps> = ({ visible, onStatusChange }) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const termRef = useRef<Terminal | null>(null);
  const fitRef = useRef<FitAddon | null>(null);
  const initRef = useRef(false);
  const eventSourceRef = useRef<EventSource | null>(null);
  const [logCount, setLogCount] = useState(0);

  // Format and write a ULF log entry to the terminal
  const writeUlfEntry = useCallback((entry: UlfLogEntry, term: Terminal) => {
    const layerConfig = LAYER_COLORS[entry.layer] || { color: COLORS.gray, arrow: '?' };
    const time = entry.ts_est || new Date(entry.ts).toLocaleTimeString('en-US', {
      hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false
    });

    // Format request_id as short hash if present
    const reqId = entry.request_id ? `⟨${entry.request_id.slice(0, 8)}⟩` : '';

    // Layer label with arrow (e.g., "RUST←" or "JAVA→")
    const layerLabel = entry.layer.replace('_IN', '').replace('_OUT', '').replace('_S', '_S');
    const arrow = layerConfig.arrow;

    // Truncate message if too long
    const msg = entry.message.length > 100 ? entry.message.slice(0, 97) + '...' : entry.message;

    // Format: [HH:MM:SS] ⟨req_id⟩ LAYER← message
    const line = `${COLORS.dim}${time}${COLORS.reset} ${COLORS.gray}${reqId.padEnd(10)}${COLORS.reset} ${layerConfig.color}${COLORS.bold}${layerLabel}${arrow}${COLORS.reset} ${msg}`;

    term.writeln(line);
    setLogCount(prev => prev + 1);
  }, []);

  // Write header to terminal
  const writeHeader = useCallback((term: Terminal) => {
    term.writeln(`${COLORS.cyan}${COLORS.bold}=== ULF - Universal Log Format ===${COLORS.reset}`);
    term.writeln(`${COLORS.dim}PZRP stack trace: Lua → Java → Rust → back${COLORS.reset}`);
    term.writeln(`${COLORS.dim}Timestamps in EST | Request IDs for correlation${COLORS.reset}`);
    term.writeln('');

    // Legend
    term.writeln(`${COLORS.dim}Legend:${COLORS.reset}`);
    term.writeln(`  ${COLORS.orange}RUST←→${COLORS.reset} Rust layer    ${COLORS.purple}JAVA←→${COLORS.reset} Java layer    ${COLORS.cyan}LUA_S←→${COLORS.reset} Server Lua    ${COLORS.green}LUA_C◆${COLORS.reset} Client Lua`);
    term.writeln('');
  }, []);

  // Initialize terminal and connect to SSE
  const initialize = useCallback(() => {
    if (initRef.current || !containerRef.current) return;
    initRef.current = true;

    const term = new Terminal({
      cols: 120,
      rows: 30,
      theme: {
        background: 'transparent',
        foreground: '#e5e7eb',
        cursor: '#06b6d4',
        cursorAccent: '#06b6d4',
        selectionBackground: '#06b6d450',
        selectionForeground: '#ffffff',
      },
      fontFamily: 'JetBrains Mono, Menlo, Monaco, "Courier New", monospace',
      fontSize: 12,
      cursorBlink: false,
      disableStdin: true, // Read-only
      scrollback: 5000,
      allowTransparency: true,
    });

    const fit = new FitAddon();
    term.loadAddon(fit);
    term.open(containerRef.current);

    setTimeout(() => fit.fit(), 100);

    termRef.current = term;
    fitRef.current = fit;

    writeHeader(term);
    onStatusChange?.('connecting');

    // Connect to ULF SSE stream
    const isHttps = window.location.protocol === 'https:';
    const sseUrl = isHttps
      ? '/api/claude-logs/api/ulf/stream'
      : 'http://localhost:3101/api/ulf/stream';

    const es = new EventSource(sseUrl);
    eventSourceRef.current = es;

    es.onopen = () => {
      onStatusChange?.('connected');
      term.writeln(`${COLORS.green}Connected to ULF stream${COLORS.reset}`);
      term.writeln('');
    };

    es.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);

        // Handle config message (layer colors)
        if (data.type === 'config' || data.type === 'session') {
          return; // Skip meta messages
        }

        // It's a log entry
        if (data.layer && data.message) {
          writeUlfEntry(data as UlfLogEntry, term);
        }
      } catch {
        // Parse error, ignore
      }
    };

    es.onerror = () => {
      onStatusChange?.('disconnected');
      term.writeln(`${COLORS.red}ULF stream disconnected - reconnecting...${COLORS.reset}`);
    };

    // Handle resize
    const handleResize = () => fit.fit();
    window.addEventListener('resize', handleResize);

    const resizeObserver = new ResizeObserver(() => fit.fit());
    if (containerRef.current) {
      resizeObserver.observe(containerRef.current);
    }

    return () => {
      window.removeEventListener('resize', handleResize);
      resizeObserver.disconnect();
      es.close();
      term.dispose();
    };
  }, [onStatusChange, writeHeader, writeUlfEntry]);

  // Initialize when first visible
  useEffect(() => {
    if (visible && !initRef.current) {
      initialize();
    }
  }, [visible, initialize]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (eventSourceRef.current) {
        eventSourceRef.current.close();
      }
      if (termRef.current) {
        termRef.current.dispose();
      }
    };
  }, []);

  // Fit when becoming visible
  useEffect(() => {
    if (visible && fitRef.current && initRef.current) {
      setTimeout(() => fitRef.current?.fit(), 50);
    }
  }, [visible]);

  return (
    <div
      ref={containerRef}
      className={`h-full w-full ${visible ? '' : 'hidden'}`}
      style={{ minHeight: '200px' }}
    />
  );
};

export default LogHistoryTerminal;
